/**
 * Detección del dispositivo para la impresión.
 *
 * isi-print es un agente de escritorio (Windows, macOS y Linux): en teléfonos y tabletas (Android,
 * iOS, iPadOS) y en ChromeOS no se puede instalar. En esos equipos no se intenta contactar al agente
 * y, en lugar del diálogo de impresión, se ofrece un menú para ver, compartir, descargar o enviar
 * el documento por WhatsApp.
 */

export type SistemaDispositivo = 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'chromeos' | 'otro'

export interface DispositivoInfo {
  sistema: SistemaDispositivo
  /** Teléfono o tableta */
  movil: boolean
  /** Se puede instalar isi-print en este equipo */
  soportaIsiPrint: boolean
}

/**
 * Forzar el modo para pruebas o equipos poco comunes (ej. una tableta con Linux):
 * localStorage['isi-print-dispositivo'] = 'escritorio' | 'movil'
 */
export const DISPOSITIVO_OVERRIDE_KEY = 'isi-print-dispositivo'

interface NavigatorUAData {
  mobile?: boolean
  platform?: string
}

export const detectarDispositivo = (): DispositivoInfo => {
  if (typeof navigator === 'undefined') return { sistema: 'otro', movil: false, soportaIsiPrint: true }
  const ua = navigator.userAgent || ''
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData
  // iPadOS 13+ se presenta como Mac de escritorio: se distingue por la pantalla táctil
  const esIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)

  let sistema: SistemaDispositivo
  if (/Android/i.test(ua)) sistema = 'android'
  else if (/iPhone|iPod/i.test(ua) || esIpad) sistema = 'ios'
  else if (/CrOS/i.test(ua)) sistema = 'chromeos'
  else if (/Windows/i.test(ua)) sistema = 'windows'
  else if (/Macintosh|Mac OS X/i.test(ua)) sistema = 'macos'
  else if (/Linux|X11/i.test(ua)) sistema = 'linux'
  else sistema = 'otro'

  const movil =
    Boolean(uaData?.mobile) ||
    sistema === 'android' ||
    sistema === 'ios' ||
    /Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)

  let soportaIsiPrint = !movil && (sistema === 'windows' || sistema === 'macos' || sistema === 'linux')

  try {
    const override = localStorage.getItem(DISPOSITIVO_OVERRIDE_KEY)
    if (override === 'escritorio') soportaIsiPrint = true
    if (override === 'movil') soportaIsiPrint = false
  } catch {
    // sin localStorage: se usa la detección
  }

  return { sistema, movil, soportaIsiPrint }
}

/** Dispositivo actual (se calcula una vez al cargar la app) */
export const DISPOSITIVO = detectarDispositivo()

/** isi-print se puede usar en este equipo (Windows, macOS o Linux de escritorio) */
export const ISI_PRINT_SOPORTADO = DISPOSITIVO.soportaIsiPrint

export const MENSAJE_ISI_PRINT_NO_SOPORTADO =
  'La impresión directa con isi-print solo está disponible en computadoras con Windows, macOS o Linux.'
